import { cn } from '@workspace/ui/lib/utils'
import { Check, CircleAlert, Clock } from 'lucide-react'
import type { ReactNode } from 'react'

export type CheckRowTone = 'ok' | 'todo' | 'stop' | 'neutral'

export interface CheckRowProps {
  children: ReactNode
  /**
   * `ok` — reassurance, the thing is fine (green check).
   * `todo` — work the founder still has to do (amber alert). Deliberately not
   *   `stop`: an unfilled profile field is not a failure, and colouring it red
   *   tells a founder something is broken when nothing is.
   * `stop` — the thing that went wrong (red alert).
   * `neutral` — waiting or informational (clock).
   */
  tone?: CheckRowTone
  className?: string
}

const TONE_ICON = {
  ok: Check,
  todo: CircleAlert,
  stop: CircleAlert,
  neutral: Clock,
} as const

const TONE_ICON_CLASS: Record<CheckRowTone, string> = {
  ok: 'text-emerald-400',
  todo: 'text-primary',
  stop: 'text-red-400',
  neutral: 'text-muted-foreground',
}

const TONE_TEXT_CLASS: Record<CheckRowTone, string> = {
  // The failure reads at full strength; the reassurances around it recede, so a
  // diagnostic list has one thing your eye lands on rather than four.
  ok: 'text-muted-foreground',
  todo: 'text-foreground font-medium',
  stop: 'text-foreground font-medium',
  neutral: 'text-muted-foreground',
}

/**
 * One line of a diagnostic or reassurance list — an icon and a sentence.
 *
 * Collapses four near-identical treatments that the design used separately
 * (progress rows, cause rows, trust rows, and the plain clock row). They only
 * ever differed by icon and emphasis, which is what `tone` now carries.
 */
export function CheckRow({ children, tone = 'ok', className }: CheckRowProps) {
  const Icon = TONE_ICON[tone]
  return (
    <div className={cn('flex items-start gap-2.5 py-1.5 text-[13px]', className)}>
      <Icon
        className={cn('mt-0.5 size-4 shrink-0', TONE_ICON_CLASS[tone])}
        strokeWidth={tone === 'ok' ? 2.4 : 2}
        aria-hidden
      />
      <span className={TONE_TEXT_CLASS[tone]}>{children}</span>
    </div>
  )
}
